import { useCallback, useMemo, type ComponentProps } from 'react';
import {
    Button,
    Icon,
    SuperButton,
    Text,
    withDialog,
    type WithDialogProps,
} from '@kidslabs/mario-nextjs-react-ui-core';
import { MoveDown } from 'lucide-react';
import styles from './cart-total.module.css';
import { useTranslation } from 'next-i18next';
import type { CartTotalViewProps, SuperButtonProps } from './cart-total.types';

export const CartTotalViewInner: React.FC<CartTotalViewProps & WithDialogProps> = ({
    finalPrice,
    originalPrice,
    discountPrice,
    onButtonClick,
    isAuthorized,
    buyNowVisible,
    checked,
    onChecked,
    setDialogOptions,
    showDialog,
    disabled,
}) => {
    const { t } = useTranslation('quote');
    const renderCartTotalActionLabel = useCallback(() => {
        return <Text>Mua ngay</Text>;
    }, []);

    // Disable button only if no items selected
    // Authentication is handled at the action level (guard in CartTotal container)
    const isButtonDisabled = useMemo(() => {
        return !buyNowVisible;
    }, [buyNowVisible]);

    const handleShowDialog = useCallback(
        ({ message }: { message?: string }) => {
            setDialogOptions({
                title: t('text_notification', 'Thông báo'),
                content: message ?? '',
                onAccept: () => {},
                acceptLabel: t('text_accept', 'Đồng ý'),
            });
            showDialog();
        },
        [setDialogOptions, showDialog, t]
    );

    const onBuyNowButtonClick = useCallback(() => {
        if (!isButtonDisabled) {
            onButtonClick({
                onError: handleShowDialog,
            });
            return;
        }
        handleShowDialog({
            message: t('text_no_items_selected', 'Vui lòng chọn sản phẩm để tiếp tục thanh toán'),
        });
    }, [isButtonDisabled, onButtonClick, handleShowDialog, t]);

    const discountViewIconProps: ComponentProps<typeof Icon> = useMemo(() => {
        return {
            children: <Icon icon={MoveDown} />,
        };
    }, []);

    const buttonProps: SuperButtonProps['buttonProps'] = useMemo(() => {
        return {
            children: renderCartTotalActionLabel(),
            onClick: onBuyNowButtonClick,
        };
    }, [onBuyNowButtonClick, renderCartTotalActionLabel]);

    const discountViewProps: SuperButtonProps['extra'] = useMemo(() => {
        if (!discountPrice) {
            return undefined;
        }

        return {
            secondaryText: discountPrice,
            primaryIconProps: discountViewIconProps,
        };
    }, [discountPrice, discountViewIconProps]);

    const renderCheckoutMobileButtonStyles = useMemo(() => {
        return isAuthorized ? styles.cartSubmitButtonLoggedIn : styles.cartSubmitButtonLoggedOut;
    }, [isAuthorized]);

    const renderCheckoutMobileButtonText = useMemo(() => {
        return isAuthorized ? 'Mua ngay' : 'Đăng nhập để tiếp tục mua hàng';
    }, [isAuthorized]);

    // ✅ CHECKBOX PROPS: Pass checked state and onCheckedChange handler (matches mobile SuperButton pattern)
    const checkBoxProps: SuperButtonProps['checkBoxProps'] = useMemo(() => {
        return {
            checked: checked ?? false,
            onCheckedChange: onChecked,
        };
    }, [checked, onChecked]);

    return (
        <div>
            <div className={styles.mobileWrapper}>
                <SuperButton
                    primaryText={finalPrice}
                    primarySubText={originalPrice}
                    buttonProps={buttonProps}
                    extra={discountViewProps}
                    checkBoxProps={checkBoxProps}
                    checkBoxLabel={t('text_all', 'Tất cả')}
                    disabled={disabled}
                />
            </div>
            <div className={styles.desktopWrapper}>
                <Button disabled={disabled} className={renderCheckoutMobileButtonStyles} onClick={onBuyNowButtonClick}>
                    {renderCheckoutMobileButtonText}
                </Button>
            </div>
        </div>
    );
};

export const CartTotalView = withDialog(CartTotalViewInner);
